import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import styles from "./AddMarkupModal.module.css";

const emptyProductForm = {
  name: "",
  productType: "Flights",
  markupType: "Percentage",
  markupValue: "",
  startDate: "",
  endDate: "",
  status: true,
};

const emptySupplierForm = {
  name: "",
  supplier: "",
  markupType: "Percentage",
  markupValue: "",
  startDate: "",
  endDate: "",
  status: true,
};

const toForm = (data, type) => {
  if (!data) return type === "product" ? emptyProductForm : emptySupplierForm;
  return {
    name: data.name || "",
    ...(type === "product"
      ? { productType: data.productType || "Flights" }
      : { supplier: data.supplier || "" }),
    markupType: data.markupType || "Percentage",
    markupValue: data.markupValue ?? "",
    startDate: data.startDate || "",
    endDate: data.endDate || "",
    status: data.status ?? true,
  };
};

const AddMarkupModal = ({ open, onClose, type = "product", initialData, onSave, saveLoading = false }) => {
  const [form, setForm] = useState(() => toForm(initialData, type));

  useEffect(() => {
    if (open) setForm(toForm(initialData, type));
  }, [open, initialData, type]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = !!initialData;

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    if (type === "supplier" && !form.supplier.trim()) return;
    if (!form.markupValue) return;
    onSave({ ...form, markupValue: parseFloat(form.markupValue) });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${isEdit ? "Edit" : "Add"} ${type === "product" ? "Product-Based" : "Supplier-Based"} Markup Rule`}
      width="560px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saveLoading}>
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <><span className={styles.spinner} aria-hidden /> Saving…</>
            ) : isEdit ? "Save Changes" : "+ Add Rule"}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>

        {/* Rule Name */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label>Rule Name</label>
          <input
            placeholder="e.g. Flight Markup Standard"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>

        {/* Product Type OR Supplier */}
        {type === "product" ? (
          <div className={styles.field}>
            <label>Product Type</label>
            <select value={form.productType} onChange={(e) => set("productType", e.target.value)}>
              <option>Flights</option>
              <option>Hotels</option>
              <option>Insurance</option>
              <option>Packages</option>
            </select>
          </div>
        ) : (
          <div className={styles.field}>
            <label>Supplier</label>
            <input
              placeholder="e.g. Amadeus"
              value={form.supplier}
              onChange={(e) => set("supplier", e.target.value)}
            />
          </div>
        )}

        {/* Markup Type */}
        <div className={styles.field}>
          <label>Markup Type</label>
          <select value={form.markupType} onChange={(e) => set("markupType", e.target.value)}>
            <option value="Percentage">Percentage (%)</option>
            <option value="Fixed">Fixed Amount (€)</option>
          </select>
        </div>

        {/* Markup Value */}
        <div className={styles.field}>
          <label>Markup Value</label>
          <div className={styles.inputWithAddon}>
            <span className={styles.addon}>
              {form.markupType === "Percentage" ? "%" : "€"}
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={form.markupType === "Percentage" ? "e.g. 12" : "e.g. 25.00"}
              value={form.markupValue}
              onChange={(e) => set("markupValue", e.target.value)}
              className={styles.addonInput}
            />
          </div>
        </div>

        {/* Start Date */}
        <div className={styles.field}>
          <label>Start Date</label>
          <input
            type="date"
            value={form.startDate}
            onChange={(e) => set("startDate", e.target.value)}
          />
        </div>

        {/* End Date */}
        <div className={styles.field}>
          <label>End Date</label>
          <input
            type="date"
            value={form.endDate}
            onChange={(e) => set("endDate", e.target.value)}
          />
        </div>

        {/* Status Toggle */}
        <div className={styles.toggleRow}>
          <div className={styles.field}>
            <label>Status</label>
            <ToggleSwitch
              checked={form.status}
              onChange={() => set("status", !form.status)}
            />
          </div>
        </div>

      </div>

      <div className={styles.infoBox}>
        ⚠️ Rules outside their validity period are never applied, regardless of their status.
      </div>
    </BaseModal>
  );
};

export default AddMarkupModal;